/**
 * saleInvoiceCoreRenderers.tsx — klon purchaseInvoiceCoreRenderers dla faktur sprzedaży.
 *
 * Mapa core slug → renderer dla pól Faktury sprzedaży w zakładce „Dane".
 * Analog do `purchaseInvoiceCoreRenderers.tsx`.
 *
 * Różnice wobec zakupu:
 *  - `received_date` renderer USUNIĘTY — backend zwraca null dla sprzedaży.
 *  - `vendor_id` picker placeholder „— wybierz nabywcę —" (kupujący, nie dostawca).
 *  - i18n namespace: tenant.saleInvoices.*
 *
 * `issue_date` / `payment_due_date` / `amount_net` / `amount_gross` — mimo typu `string`
 * (niepusty) w `SaleInvoiceFormData`, backend potrafi zwrócić `null` dla faktur
 * zaimportowanych bez tych danych (KSeF/Symfonia — kolumny DB są nullable).
 * `value={... ?? ''}` trzyma input kontrolowany od pierwszego renderu — bez tego React
 * ostrzega "changing an uncontrolled input to be controlled". Stan `form` i payload
 * zapisu NIE są dotykane (`?? ''` jest tylko w warstwie wyświetlania).
 *
 * F12.D2 (Etap 4d) — POZOSTAJE hardcoded (zakres zamknięty w tym etapie):
 * brak seeded dictionary `sale_invoice_*` w seed-pub.ts (tylko `purchase_invoice_*`
 * istnieją). Kody enumów są identyczne z zakupem, więc `document_type`/`urgency`/
 * `payment_method` MOGŁYBY czytać z `purchase_invoice_*` dictionaries (wspólny
 * namespace `documents.<klasa faktury>.*` już istnieje dla uprawnień), ale to decyzja
 * produktowa (czy sprzedaż ma dzielić słownik z zakupem, czy dostać własny) —
 * nie została podjęta w tym etapie. Tworzenie nowego `sale_invoice_*` dictionary
 * jest poza zakresem. Zob. raport Etapu 4d.
 */

import { type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DOCUMENT_URGENCIES,
  INVOICE_CURRENCIES,
  INVOICE_KINDS,
  INVOICE_STATUSES,
  KSEF_ISSUE_MODES,
  SALE_INVOICE_PAYMENT_METHODS,
} from '@saas/shared';

import { CoreInput, type CoreInputProps } from '@/components/CoreInput';
import { CORE_INPUT_CLASS_MUTED_DISABLED } from '@/components/coreRendererClasses';
import { CodedSelect, formatTimestampPl } from '@/components/documents/codedFieldControls';
import { Dropdown } from '@/components/Dropdown';
import {
  buildVendorAutoFillPatch,
  VendorTablePickerButton,
} from '@/components/invoices/vendorPicker';
import { type CoreFieldMeta, Field, Section } from '@/components/kartoteki/CoreFieldLayout';
import { RecordCategoryPicker } from '@/components/record-categories/RecordCategoryPicker';
import { ContractorSearchPicker } from '@/components/ui/ContractorSearchPicker';
import { OpenRecordCardButton } from '@/components/ui/OpenRecordCardButton';
import { UserPicker } from '@/components/ui/UserPicker';
import { UserTablePickerButton } from '@/components/ui/UserTablePickerButton';
import { documentUrgencyLabel } from '@/lib/documents/document-select-labels';
import {
  invoicePaymentMethodLabel,
  invoiceStatusLabel,
} from '@/lib/documents/invoice-select-labels';
import { ksefIssueModeLabel } from '@/lib/documents/ksef-jpk-markers';

import type { SaleInvoiceFormData } from './saleInvoiceFormTypes';
import { SaleSourceBadge } from './SaleSourceBadge';

export { Field, Section };

export interface SaleInvoiceRendererProps {
  field: CoreFieldMeta;
  form: SaleInvoiceFormData;
  set: <K extends keyof SaleInvoiceFormData>(key: K, value: SaleInvoiceFormData[K]) => void;
  /** Atomowy zapis wielu pól w jednym `onChange` (unika nadpisywania się kolejnych `set`). */
  setMany: (patch: Partial<SaleInvoiceFormData>) => void;
  /** Czy dane pole jest oznaczone jako nieprawidłowe (ramka) — puste wymagane / zły format / odrzucone przez serwer. */
  error: (key: keyof SaleInvoiceFormData) => boolean;
  /** Komunikat błędu pod polem (czerwony tekst) — konkretny powód po polsku; `undefined` = brak błędu. */
  errorText: (key: keyof SaleInvoiceFormData) => string | undefined;
  editable: boolean;
  isCreate: boolean;
  /** Dokument ma pozycje → kwoty nagłówka wyliczane serwerowo z linii, pola kwot read-only. */
  hasItems?: boolean;
  /**
   * Firma wybrana w create (z URL, patrz SaleInvoiceDetail) — przekazywana jawnie do
   * `ContractorSearchPicker` (`vendor_id`), żeby picker w trybie „Wszystkie firmy" pokazał
   * kontrahentów WŁAŚCIWEJ firmy, nie unii wszystkich firm usera. `undefined`/`null` w edit
   * (bez zmiany zachowania — firma pochodzi wtedy z rekordu, nie z tego propa).
   */
  companyId?: string | null;
}

type Renderer = (props: SaleInvoiceRendererProps) => ReactNode;

// ─── Słowniki (enum stringi) ────────────────────────────────────────────────

// Listy kodów (rodzaj faktury, status, pilność, sposób płatności, waluta) pochodzą z `@saas/shared`
// — to samo źródło czytają kolumny i filtry list oraz walidacja tras API. Nazwy kodów: funkcje
// z `lib/documents/*-select-labels` (te same na formularzu, w kolumnie i w filtrze); waluta
// pokazuje sam kod („PLN”).

// ─── Helpery sub-komponenty ─────────────────────────────────────────────────

const inputClass = CORE_INPUT_CLASS_MUTED_DISABLED;

// Klasa nakładana na pole z błędem walidacji (puste pole wymagane przy zapisie).
const invalidInputClass = 'border-red-500 ring-1 ring-red-500';

function TextInput(props: Omit<CoreInputProps, 'variant'>) {
  return <CoreInput variant="mutedDisabled" {...props} />;
}

function Textarea({
  value,
  onChange,
  disabled,
  testId,
  rows = 3,
  placeholder,
}: {
  value: string;
  onChange: (v: string) => void;
  disabled?: boolean;
  testId?: string;
  rows?: number;
  placeholder?: string;
}) {
  return (
    <textarea
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      rows={rows}
      placeholder={placeholder}
      data-testid={testId}
      className={inputClass}
    />
  );
}

function SelectInput({
  value,
  onChange,
  options,
  labelFor,
  disabled,
  testId,
  withEmpty = false,
  emptyLabel = '— wybierz —',
  invalid,
}: {
  value: string;
  onChange: (v: string) => void;
  options: readonly string[];
  /** Nazwa kodu na liście; bez niej opcja pokazuje sam kod (waluta). */
  labelFor?: (code: string, t: (key: string) => string) => string;
  disabled?: boolean;
  testId?: string;
  withEmpty?: boolean;
  emptyLabel?: string;
  invalid?: boolean;
}) {
  // Hook w pod-komponencie, bo czyste lambdy rendererów nie mogą go wywołać.
  const { t } = useTranslation();
  return (
    <Dropdown
      options={[
        ...(withEmpty ? [{ value: '', label: emptyLabel }] : []),
        ...options.map((opt) => ({ value: opt, label: labelFor ? labelFor(opt, t) : opt })),
      ]}
      value={value}
      onChange={onChange}
      disabled={disabled}
      data-testid={testId}
      className={invalid ? invalidInputClass : undefined}
    />
  );
}

function DocumentTypeSelect({
  value,
  onChange,
  disabled,
  invalid,
}: {
  value: string;
  onChange: (v: string) => void;
  disabled?: boolean;
  invalid?: boolean;
}) {
  const { t } = useTranslation();
  return (
    <Dropdown
      options={INVOICE_KINDS.map((code) => ({
        value: code,
        label: t(`tenant.saleInvoices.invoiceKindValues.${code}`, { defaultValue: code }),
      }))}
      value={value}
      onChange={onChange}
      disabled={disabled}
      data-testid="si-field-documentType"
      className={invalid ? invalidInputClass : undefined}
    />
  );
}

// ─── Mapa renderers (slug → JSX) ────────────────────────────────────────────

export const SALE_INVOICE_CORE_RENDERERS: Record<string, Renderer> = {
  // — Identyfikacja —
  system_number: (p) => (
    <Field label={p.field.name} required={false}>
      <TextInput
        testId="si-field-systemNumber"
        value={p.form.systemNumber ?? ''}
        onChange={() => {}}
        disabled
        placeholder={p.isCreate ? 'Nadany po zapisie' : ''}
      />
    </Field>
  ),

  invoice_number: (p) => (
    <Field label={p.field.name} required={p.field.isRequired}>
      <TextInput
        testId="si-field-invoiceNumber"
        value={p.form.invoiceNumber}
        onChange={(v) => p.set('invoiceNumber', v)}
        disabled={!p.editable}
        maxLength={50}
      />
    </Field>
  ),

  ksef_number: (p) => (
    <Field label={p.field.name} required={p.field.isRequired}>
      <TextInput
        testId="si-field-ksefNumber"
        value={p.form.ksefNumber ?? ''}
        onChange={(v) => p.set('ksefNumber', v || null)}
        disabled={!p.editable}
        maxLength={40}
      />
    </Field>
  ),

  document_type: (p) => (
    <Field label={p.field.name} required={p.field.isRequired} error={p.errorText('documentType')}>
      <DocumentTypeSelect
        value={p.form.documentType}
        onChange={(v) => p.set('documentType', v)}
        disabled={!p.editable}
        invalid={p.error('documentType')}
      />
    </Field>
  ),

  status: (p) => (
    <Field label={p.field.name} required={p.field.isRequired} error={p.errorText('status')}>
      <SelectInput
        testId="si-field-status"
        value={p.form.status}
        onChange={(v) => p.set('status', v)}
        options={INVOICE_STATUSES}
        labelFor={invoiceStatusLabel}
        disabled={!p.editable}
        invalid={p.error('status')}
      />
    </Field>
  ),

  urgency: (p) => (
    <Field label={p.field.name} required={p.field.isRequired} error={p.errorText('urgency')}>
      <SelectInput
        testId="si-field-urgency"
        value={p.form.urgency}
        onChange={(v) => p.set('urgency', v)}
        options={DOCUMENT_URGENCIES}
        labelFor={documentUrgencyLabel}
        disabled={!p.editable}
        invalid={p.error('urgency')}
      />
    </Field>
  ),

  corrected_invoice_id: (p) => (
    <Field label={p.field.name} required={p.field.isRequired}>
      <TextInput
        testId="si-field-correctedInvoiceId"
        value={p.form.correctedInvoiceId ?? ''}
        onChange={(v) => p.set('correctedInvoiceId', v || null)}
        disabled={!p.editable}
        placeholder="UUID faktury pierwotnej (jeśli korekta)"
      />
    </Field>
  ),

  // — Daty —
  issue_date: (p) => (
    <Field label={p.field.name} required={p.field.isRequired} error={p.errorText('issueDate')}>
      <TextInput
        testId="si-field-issueDate"
        type="date"
        value={p.form.issueDate ?? ''}
        onChange={(v) => p.set('issueDate', v)}
        disabled={!p.editable}
        invalid={p.error('issueDate')}
      />
    </Field>
  ),

  // received_date INTENTIONALLY ABSENT — null for sale invoices

  sale_date: (p) => (
    <Field label={p.field.name} required={p.field.isRequired}>
      <TextInput
        testId="si-field-saleDate"
        type="date"
        value={p.form.saleDate ?? ''}
        onChange={(v) => p.set('saleDate', v || null)}
        disabled={!p.editable}
      />
    </Field>
  ),

  payment_due_date: (p) => (
    <Field label={p.field.name} required={p.field.isRequired} error={p.errorText('paymentDueDate')}>
      <TextInput
        testId="si-field-paymentDueDate"
        type="date"
        value={p.form.paymentDueDate ?? ''}
        onChange={(v) => p.set('paymentDueDate', v)}
        disabled={!p.editable}
        invalid={p.error('paymentDueDate')}
      />
    </Field>
  ),

  // — Nabywca (accessor field names vendorId/vendorNameCached/vendorTaxIdCached UNCHANGED) —
  // T3: dwie ścieżki wyboru (inline type-ahead + przycisk otwierający tabelowy
  // picker rekordów), OBIE wołają WSPÓLNY helper `buildVendorAutoFillPatch`
  // (patrz `components/invoices/vendorPicker.tsx`) — jeden kontrakt zapisu
  // cache'u nazwy/NIP dla obu ścieżek i obu klas faktur.
  vendor_id: (p) => (
    <Field
      label={p.field.name}
      required={p.field.isRequired}
      colSpan={2}
      error={p.errorText('vendorId')}
    >
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <ContractorSearchPicker
            data-testid="si-field-vendor"
            value={p.form.vendorId}
            selectedLabel={p.form.vendorNameCached}
            companyId={p.companyId}
            onChange={(id, vendor) => p.setMany(buildVendorAutoFillPatch(id, vendor))}
            disabled={!p.editable}
            invalid={p.error('vendorId')}
          />
        </div>
        <VendorTablePickerButton
          companyId={p.companyId}
          value={p.form.vendorId}
          disabled={!p.editable}
          onPick={(id, vendor) => p.setMany(buildVendorAutoFillPatch(id, vendor))}
          data-testid="si-field-vendor-open-picker"
        />
        <OpenRecordCardButton
          systemSlug="contractors"
          recordId={p.form.vendorId}
          testId="si-field-vendor-open-card"
        />
      </div>
    </Field>
  ),

  vendor_tax_id_cached: (p) => (
    <Field label={p.field.name} required={false}>
      <TextInput
        testId="si-field-vendorTaxIdCached"
        value={p.form.vendorTaxIdCached ?? ''}
        onChange={(v) => p.set('vendorTaxIdCached', v || null)}
        disabled={!p.editable}
        maxLength={20}
      />
    </Field>
  ),

  vendor_name_cached: (p) => (
    <Field label={p.field.name} required={false}>
      <TextInput
        testId="si-field-vendorNameCached"
        value={p.form.vendorNameCached ?? ''}
        onChange={(v) => p.set('vendorNameCached', v || null)}
        disabled={!p.editable}
        maxLength={500}
      />
    </Field>
  ),

  // — Kwoty —
  currency_code: (p) => (
    <Field label={p.field.name} required={p.field.isRequired} error={p.errorText('currencyCode')}>
      <SelectInput
        testId="si-field-currency"
        value={p.form.currencyCode}
        onChange={(v) => p.set('currencyCode', v)}
        options={INVOICE_CURRENCIES}
        disabled={!p.editable}
        invalid={p.error('currencyCode')}
      />
    </Field>
  ),

  exchange_rate: (p) => (
    <Field label={p.field.name} required={p.field.isRequired}>
      <TextInput
        testId="si-field-exchangeRate"
        type="number"
        step="0.0001"
        min="0"
        value={p.form.exchangeRate ?? ''}
        onChange={(v) => p.set('exchangeRate', v || null)}
        disabled={!p.editable}
        placeholder={p.form.currencyCode === 'PLN' ? 'Nie wymagany dla PLN' : ''}
      />
    </Field>
  ),

  amount_net: (p) => (
    <Field label={p.field.name} required={p.field.isRequired} error={p.errorText('amountNet')}>
      <TextInput
        testId="si-field-amountNet"
        type="number"
        step="0.01"
        min="0"
        value={p.form.amountNet ?? ''}
        onChange={(v) => p.set('amountNet', v)}
        disabled={!p.editable || !!p.hasItems}
        invalid={p.error('amountNet')}
      />
    </Field>
  ),

  amount_vat: (p) => (
    <Field label={p.field.name} required={p.field.isRequired}>
      <TextInput
        testId="si-field-amountVat"
        type="number"
        step="0.01"
        min="0"
        value={p.form.amountVat ?? ''}
        onChange={(v) => p.set('amountVat', v || null)}
        disabled={!p.editable || !!p.hasItems}
      />
    </Field>
  ),

  amount_gross: (p) => (
    <Field label={p.field.name} required={p.field.isRequired} error={p.errorText('amountGross')}>
      <TextInput
        testId="si-field-amountGross"
        type="number"
        step="0.01"
        min="0"
        value={p.form.amountGross ?? ''}
        onChange={(v) => p.set('amountGross', v)}
        disabled={!p.editable || !!p.hasItems}
        invalid={p.error('amountGross')}
      />
    </Field>
  ),

  // — Płatność —
  payment_method: (p) => (
    <Field label={p.field.name} required={p.field.isRequired}>
      <SelectInput
        testId="si-field-paymentMethod"
        value={p.form.paymentMethod ?? ''}
        onChange={(v) => p.set('paymentMethod', v || null)}
        options={SALE_INVOICE_PAYMENT_METHODS}
        labelFor={invoicePaymentMethodLabel}
        disabled={!p.editable}
        withEmpty
      />
    </Field>
  ),

  paid_at: (p) => (
    <Field label={p.field.name} required={p.field.isRequired}>
      <TextInput
        testId="si-field-paidAt"
        type="date"
        value={p.form.paidAt ?? ''}
        onChange={(v) => p.set('paidAt', v || null)}
        disabled={!p.editable}
      />
    </Field>
  ),

  vendor_bank_account_id: (p) => (
    <Field label={p.field.name} required={p.field.isRequired}>
      <TextInput
        testId="si-field-vendorBankAccountId"
        value={p.form.vendorBankAccountId ?? ''}
        onChange={(v) => p.set('vendorBankAccountId', v || null)}
        disabled={!p.editable}
        placeholder="UUID konta bankowego (T16.8 picker)"
      />
    </Field>
  ),

  payment_notes: (p) => (
    <Field label={p.field.name} required={p.field.isRequired} colSpan={2}>
      <Textarea
        testId="si-field-paymentNotes"
        value={p.form.paymentNotes ?? ''}
        onChange={(v) => p.set('paymentNotes', v || null)}
        disabled={!p.editable}
        rows={2}
      />
    </Field>
  ),

  // — Inne —
  description: (p) => (
    <Field label={p.field.name} required={p.field.isRequired} colSpan={2}>
      <TextInput
        testId="si-field-description"
        value={p.form.description ?? ''}
        onChange={(v) => p.set('description', v || null)}
        disabled={!p.editable}
        placeholder="Krótki tytuł (np. Hosting + domena)"
      />
    </Field>
  ),

  cost_center_default: (p) => (
    <Field label={p.field.name} required={p.field.isRequired}>
      <TextInput
        testId="si-field-costCenterDefault"
        value={p.form.costCenterDefault ?? ''}
        onChange={(v) => p.set('costCenterDefault', v || null)}
        disabled={!p.editable}
        maxLength={50}
      />
    </Field>
  ),

  rejection_reason: (p) => (
    <Field label={p.field.name} required={p.field.isRequired} colSpan={2}>
      <Textarea
        testId="si-field-rejectionReason"
        value={p.form.rejectionReason ?? ''}
        onChange={(v) => p.set('rejectionReason', v || null)}
        disabled={!p.editable}
        rows={2}
      />
    </Field>
  ),

  notes: (p) => (
    <Field label={p.field.name} required={p.field.isRequired} colSpan={2}>
      <Textarea
        testId="si-field-notes"
        value={p.form.notes ?? ''}
        onChange={(v) => p.set('notes', v || null)}
        disabled={!p.editable}
        rows={3}
      />
    </Field>
  ),

  // — Opiekun rekordu (T20D, parytet z umowy/pisma/handlowe/wewnętrzne) —
  owner_user_id: (p) => (
    <Field label={p.field.name} required={false}>
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <UserPicker
            value={p.form.ownerUserId}
            onChange={(v) => p.set('ownerUserId', v)}
            companyId={p.companyId}
            disabled={!p.editable}
            data-testid="si-field-ownerUserId"
          />
        </div>
        <UserTablePickerButton
          value={p.form.ownerUserId}
          onPick={(id) => p.set('ownerUserId', id)}
          companyId={p.companyId}
          disabled={!p.editable}
          data-testid="si-field-ownerUserId-open-picker"
        />
      </div>
    </Field>
  ),

  // — Kategoria (oś `record_categories`, kartoteka_key='documents' — wspólna dla
  //   wszystkich 7 klas dokumentów). RecordCategoryPicker rysuje własny <Field>
  //   (label + dropdown), więc renderer NIE owija go drugi raz (mirror
  //   documentCategoryRenderer.tsx — kontrakt InvoiceRenderer używa `set`, nie
  //   engine `setField`, więc adapter jest lokalny do tego rejestru).
  category_id: (p) => (
    <RecordCategoryPicker
      kartotekaKey="documents"
      value={p.form.categoryId}
      onChange={(next) => p.set('categoryId', next)}
      editable={p.editable}
      testId="si-field-category"
    />
  ),

  // — Źródło (T21 finding CRITICAL, mirror purchaseInvoiceCoreRenderers.tsx — patrz
  //   komentarz tam dla pełnego uzasadnienia). Read-only: PATCH_BODY_SCHEMA (route.ts) nie
  //   przyjmuje tych pól — `source` ustawia backend przy tworzeniu ('manual') lub imporcie
  //   ERP/KSeF ('symfonia'/'ksef'); `external_id`/`external_source` to identyfikatory
  //   idempotency importu. `source` reużywa tego samego badge'a co nagłówek strony
  //   (SaleSourceBadge).
  source: (p) => (
    <Field label={p.field.name} required={false}>
      <SaleSourceBadge source={p.form.source} />
    </Field>
  ),

  external_id: (p) => (
    <Field label={p.field.name} required={false}>
      <TextInput
        testId="si-field-externalId"
        value={p.form.externalId ?? ''}
        onChange={() => {}}
        disabled
        maxLength={100}
      />
    </Field>
  ),

  external_source: (p) => (
    <Field label={p.field.name} required={false}>
      <TextInput
        testId="si-field-externalSource"
        value={p.form.externalSource ?? ''}
        onChange={() => {}}
        disabled
        maxLength={50}
      />
    </Field>
  ),

  // ── Sekcja KSeF (0159) — podzbiór kierunkowo neutralny ─────────────────────
  // Znaczniki ewidencji ZAKUPU (znacznik ewidencji, oznaczenie JPK, import towarów, kanał
  // wpływu) świadomie NIE MAJĄ tu odpowiednika: sprzedaż ma własne, inne zbiory ustawowe.
  ksef_issue_date: (p) => (
    <Field label={p.field.name} required={p.field.isRequired}>
      <TextInput
        testId="si-field-ksefIssueDate"
        type="date"
        value={p.form.ksefIssueDate ?? ''}
        onChange={(v) => p.set('ksefIssueDate', v || null)}
        disabled={!p.editable}
      />
    </Field>
  ),

  ksef_issue_mode: (p) => (
    <Field label={p.field.name} required={p.field.isRequired}>
      <CodedSelect
        testId="si-field-ksefIssueMode"
        value={p.form.ksefIssueMode ?? ''}
        onChange={(v) => p.set('ksefIssueMode', v || null)}
        codes={KSEF_ISSUE_MODES}
        labelFor={ksefIssueModeLabel}
        disabled={!p.editable}
      />
    </Field>
  ),

  // Para wypełniana wysyłką faktury do KSeF — `disabled` BEZ oglądania się na `p.editable`
  // (wzorzec `external_id`/`external_source` wyżej). Trasa zapisu nie przyjmuje tych pól
  // w body, a katalog ma je jako tylko-do-odczytu; to trzecia, widoczna linia obrony.
  ksef_submitted_at: (p) => (
    <Field label={p.field.name} required={false}>
      <TextInput
        testId="si-field-ksefSubmittedAt"
        value={formatTimestampPl(p.form.ksefSubmittedAt)}
        onChange={() => {}}
        disabled
      />
    </Field>
  ),

  ksef_available_at: (p) => (
    <Field label={p.field.name} required={false}>
      <TextInput
        testId="si-field-ksefAvailableAt"
        value={formatTimestampPl(p.form.ksefAvailableAt)}
        onChange={() => {}}
        disabled
      />
    </Field>
  ),
};

export function hasSaleInvoiceCoreRenderer(slug: string): boolean {
  return slug in SALE_INVOICE_CORE_RENDERERS;
}
